import {
  Button,
  Card,
  CardContent,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sea-snag/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Bell, CheckCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { ActivityRow } from '../../components/activity/activity-row';
import { LoadingState } from '../../components/loading-state';
import { useRequiredOrganization } from '../../hooks/use-required-organization';
import { type NotificationItem, notificationsApi } from '../../lib/api-client';
import { extractNotificationPayload } from '../../lib/notifications';
import { queryKeys, STALE_TIMES } from '../../lib/query-keys';

export const Route = createFileRoute('/$orgPid/activity')({
  component: ActivityPage,
});

function ActivityPage() {
  const { organization, isLoading } = useRequiredOrganization();

  if (isLoading) return <LoadingState />;

  return <ActivityContent orgPid={organization.pid} />;
}

const TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'price_alert', label: 'Price Alerts' },
  { value: 'comp_set_alert', label: 'Competitor Set Alerts' },
];

function ActivityContent({ orgPid }: { orgPid: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState('');
  const [direction, setDirection] = useState<'all' | 'drops' | 'increases'>('all');
  const [boatSearch, setBoatSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.notifications.list(orgPid, page, typeFilter || undefined),
    queryFn: () =>
      notificationsApi.list({
        page,
        limit: 20,
        ...(typeFilter && { type: typeFilter }),
      }),
    staleTime: STALE_TIMES.SHORT,
  });

  const { data: unreadData } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(orgPid),
    queryFn: () => notificationsApi.unreadCount(),
    staleTime: STALE_TIMES.SHORT,
  });

  const markReadMutation = useMutation({
    mutationFn: (pid: string) => notificationsApi.markRead(pid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
      toast.success(
        `Marked ${result.updated} notification${result.updated !== 1 ? 's' : ''} as read`
      );
    },
    onError: () => toast.error('Failed to mark notifications as read'),
  });

  const notifications = data?.data ?? [];
  const meta = data?.meta;
  const unreadCount = unreadData?.count ?? 0;

  const filteredNotifications = useMemo(() => {
    const search = boatSearch.trim().toLowerCase();
    return notifications.filter((n) => {
      const payload = extractNotificationPayload(n);

      if (direction !== 'all') {
        if (typeof payload.changePct !== 'number') return false;
        if (direction === 'drops' && payload.changePct >= 0) return false;
        if (direction === 'increases' && payload.changePct <= 0) return false;
      }

      if (search) {
        const boatName = payload.marketBoatName?.toLowerCase() ?? '';
        if (!boatName.includes(search)) return false;
      }

      return true;
    });
  }, [notifications, direction, boatSearch]);

  const grouped = groupByDate(filteredNotifications);

  function handleNotificationClick(notification: NotificationItem) {
    if (!notification.readAt) {
      markReadMutation.mutate(notification.id);
    }

    const payload = notification.payload;
    if (!payload) return;

    const compSetPid = payload['compSetPid'] as string | undefined;
    if (compSetPid) {
      // Price-alert payloads carry `dateFrom` (the week the change happened);
      // pass it through so the comp-set page lands on that week instead of
      // the default current-week (issue #126).
      const weekStart = payload['dateFrom'] as string | undefined;
      void navigate({
        to: '/$orgPid/competitor-sets/$pid',
        params: { orgPid, pid: compSetPid },
        ...(weekStart && { search: { weekStart } }),
      });
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Activity</h1>
          <p className="text-muted-foreground">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`
              : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isPending}
          >
            <CheckCheck className="mr-1 size-4" />
            Mark all as read
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={typeFilter === '' ? 'all' : typeFilter}
          onValueChange={(v) => {
            setTypeFilter(v === 'all' ? '' : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[180px]" aria-label="Notification type filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <SelectItem key={opt.value || 'all'} value={opt.value || 'all'}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div
          role="group"
          aria-label="Direction filter"
          className="inline-flex items-center rounded-lg border border-input bg-background p-0.5 text-sm"
        >
          {(['all', 'drops', 'increases'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setDirection(value)}
              className={`rounded-md px-3 py-1 transition-colors ${
                direction === value
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {value === 'all' ? 'All' : value === 'drops' ? 'Drops' : 'Increases'}
            </button>
          ))}
        </div>

        <Input
          aria-label="Search boats"
          placeholder="Search boat..."
          value={boatSearch}
          onChange={(e) => setBoatSearch(e.target.value)}
          className="ml-auto h-9 w-48"
        />
      </div>

      {/* Notification list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading notifications...</p>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Bell className="mb-3 size-12 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">
              {notifications.length === 0
                ? 'No notifications yet'
                : 'No notifications match your filters'}
            </p>
            <p className="text-muted-foreground text-xs">
              {notifications.length === 0
                ? 'Configure price alerts to get notified about market changes.'
                : 'Adjust the filters above or wait for new market activity.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.label}>
              <h3 className="text-muted-foreground mb-2 text-sm font-medium">{group.label}</h3>
              <div className="space-y-1.5">
                {group.items.map((notification) => (
                  <ActivityRow
                    key={notification.id}
                    notification={notification}
                    onClick={handleNotificationClick}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta && meta.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            Page {meta.page} of {meta.totalPages}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= meta.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──

function getDateGroup(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86_400_000);
  const weekStart = new Date(today.getTime() - today.getDay() * 86_400_000);

  const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (dateDay.getTime() >= today.getTime()) return 'Today';
  if (dateDay.getTime() >= yesterday.getTime()) return 'Yesterday';
  if (dateDay.getTime() >= weekStart.getTime()) return 'This Week';
  return 'Older';
}

interface DateGroup {
  label: string;
  items: NotificationItem[];
}

function groupByDate(notifications: NotificationItem[]): DateGroup[] {
  const groups = new Map<string, NotificationItem[]>();
  const order = ['Today', 'Yesterday', 'This Week', 'Older'];

  for (const n of notifications) {
    const label = getDateGroup(n.createdAt);
    const existing = groups.get(label);
    if (existing) {
      existing.push(n);
    } else {
      groups.set(label, [n]);
    }
  }

  return order
    .filter((label) => groups.has(label))
    .map((label) => ({ label, items: groups.get(label)! }));
}
